import { useEffect, useState } from 'react'
import {
  ArrowPathIcon,
  EnvelopeIcon,
  PlusIcon,
  TrashIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline'
import {
  addAuthorizedUser,
  getAuthorizedUsers,
  reinviteAuthorizedUser,
  removeAuthorizedUser,
  updateAuthorizedUser,
  type AuthorizedUser,
} from '@/services/authorizedUsers'
import { formatApiError } from '@/utils/apiError'
import { useAuth } from '@/contexts/AuthContext'
import SettingsSection from '@/components/SettingsSection'
import { toast } from '@/store/toastStore'
import {
  isAdmin,
  isSuperAdminEmail,
  ROLE_OPTIONS,
  type UserRole,
} from '@/utils/pagePermissions'

export default function AuthorizedUsersSection() {
  const { user } = useAuth()
  const [users, setUsers] = useState<AuthorizedUser[]>([])
  const [loading, setLoading] = useState(true)
  const [newEmail, setNewEmail] = useState('')
  const [newRole, setNewRole] = useState<UserRole>('user')
  const [saving, setSaving] = useState(false)
  const [reinvitingId, setReinvitingId] = useState<number | null>(null)

  const canManage = isAdmin(user)

  const loadUsers = async () => {
    if (!canManage) {
      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const response = await getAuthorizedUsers()
      setUsers(response.users)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to load authorized users')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadUsers()
  }, [canManage])

  const handleAdd = async () => {
    const email = newEmail.trim()
    if (!email || !canManage) return

    setSaving(true)
    try {
      await addAuthorizedUser(email, newRole)
      setNewEmail('')
      setNewRole('user')
      await loadUsers()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add user')
    } finally {
      setSaving(false)
    }
  }

  const handleRoleChange = async (authorizedUser: AuthorizedUser, role: UserRole) => {
    if (!canManage || authorizedUser.role === role) return

    setSaving(true)
    try {
      await updateAuthorizedUser(authorizedUser.id, { role })
      await loadUsers()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update role')
    } finally {
      setSaving(false)
    }
  }

  const handleReinvite = async (authorizedUser: AuthorizedUser) => {
    if (!canManage || authorizedUser.email === user?.email) return

    setReinvitingId(authorizedUser.id)
    try {
      const result = await reinviteAuthorizedUser(authorizedUser.id)
      toast.success(`Invited ${result.email}`)
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to re-send welcome email'))
    } finally {
      setReinvitingId(null)
    }
  }

  const handleRemove = async (authorizedUser: AuthorizedUser) => {
    if (!canManage) return

    setSaving(true)
    try {
      await removeAuthorizedUser(authorizedUser.id)
      await loadUsers()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove user')
    } finally {
      setSaving(false)
    }
  }

  const sectionIcon = (
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-100/90 ring-1 ring-violet-200/50">
      <UserGroupIcon className="h-5 w-5 text-violet-700" />
    </div>
  )

  if (!canManage) {
    return null
  }

  return (
    <SettingsSection
      title="Authorized users"
      description={
        <>
          BookKeeper uses Google sign-in. Add a Gmail address here to let that person log in; anyone not on
          this list will be denied at the login screen. Assign a role to control page access via the
          permissions matrix below. Individual page exceptions can be set in User overrides.
        </>
      }
      icon={sectionIcon}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch sm:max-w-2xl">
        <input
          type="email"
          className="input !w-full min-w-0 flex-1 h-10 text-sm sm:!w-auto"
          placeholder="user@gmail.com"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          disabled={saving}
        />
        <select
          className="input select !w-full shrink-0 h-10 text-sm sm:!w-28"
          value={newRole}
          onChange={(e) => setNewRole(e.target.value as UserRole)}
          disabled={saving}
        >
          {ROLE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => void handleAdd()}
          disabled={saving || !newEmail.trim()}
          className="btn btn-primary shrink-0 text-sm"
        >
          <PlusIcon className="h-4 w-4" />
          Add user
        </button>
      </div>

      <div className="mt-6">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <ArrowPathIcon className="h-4 w-4 animate-spin" />
            Loading allowlist…
          </div>
        ) : users.length === 0 ? (
          <p className="text-sm text-gray-500">No authorized users yet.</p>
        ) : (
          <ul className="divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200">
            {users.map((authorizedUser) => {
              const isSelf = authorizedUser.email === user?.email
              const isSuperAdmin = isSuperAdminEmail(authorizedUser.email)
              return (
                <li
                  key={authorizedUser.id}
                  className="flex flex-col gap-3 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-gray-900">{authorizedUser.email}</p>
                    {isSelf ? <p className="text-xs text-gray-500">Signed in as you</p> : null}
                    {isSuperAdmin ? (
                      <p className="text-xs text-gray-500">Super admin (role locked)</p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <select
                      className="input !w-28 text-sm"
                      value={isSuperAdmin ? 'admin' : authorizedUser.role}
                      disabled={saving || isSuperAdmin}
                      onChange={(e) =>
                        void handleRoleChange(authorizedUser, e.target.value as UserRole)
                      }
                      title={isSuperAdmin ? 'Super admin role cannot be changed' : 'User role'}
                    >
                      {ROLE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => void handleReinvite(authorizedUser)}
                      disabled={saving || isSelf || reinvitingId === authorizedUser.id}
                      className="btn btn-secondary shrink-0 text-sm disabled:opacity-40"
                      title={
                        isSelf
                          ? 'You cannot re-invite your own account'
                          : 'Re-send welcome email'
                      }
                    >
                      {reinvitingId === authorizedUser.id ? (
                        <ArrowPathIcon className="h-4 w-4 animate-spin" />
                      ) : (
                        <EnvelopeIcon className="h-4 w-4" />
                      )}
                      Re-invite
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleRemove(authorizedUser)}
                      disabled={saving || isSelf || isSuperAdmin}
                      className="btn btn-secondary shrink-0 text-sm text-red-600 hover:bg-red-50 disabled:opacity-40"
                      title={
                        isSuperAdmin
                          ? 'Super admin cannot be removed'
                          : isSelf
                            ? 'You cannot remove your own account'
                            : 'Remove user'
                      }
                    >
                      <TrashIcon className="h-4 w-4" />
                      Remove
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </SettingsSection>
  )
}
